import type { PurchaseOrder, PurchaseOrdersResponse, PurchaseOrderFilters, PurchaseOrderStats, PurchaseOrderAttachment, PurchaseOrderComment, PurchaseOrderInvoice } from '@/types/purchaseOrder.types'
import { httpRequest, construirQuery } from '@/shared/lib/http'
import type { PurchaseOrderLine, PurchaseOrderPayment } from '@/types/purchaseOrder.types'

/**
 * Órdenes de compra del portal del proveedor.
 *
 * Todas usan el token del proveedor y se pide explícito: el detalle de orden es
 * el mismo componente para el portal y para el panel interno, así que deducirlo
 * de la ruta mandaría el equivocado.
 */
const comoProveedor = { token: 'proveedor' } as const

/**
 * Documentos que un proveedor de tipo Fletes adjunta a cada factura (SP-221).
 */
export interface DocumentosDeFlete {
  cartaPorteAplica: boolean
  cartaPorteXml?: File | null
  cartaPortePdf?: File | null
  remisionFirmada?: File | null
  hojaEntregas?: File | null
}

const base = '/suppliers/purchase-orders'

/** Respuesta de `invoicing-status`: el cuerpo del bloqueo es el del 422. */
export interface EstadoDeFacturacion {
  bloqueado: boolean
  tipo?: string
  type?: string
  mensaje?: string
  facturas?: { folio: string; fecha_pago: string; dias_habiles_vencidos?: number; orden_id?: string }[]
  estado?: string
  status?: string
  mes?: string
  month?: number
  year?: number
}

export const purchaseOrdersService = {
  async getOrders(filters?: PurchaseOrderFilters): Promise<PurchaseOrdersResponse> {
    const query = construirQuery({ ...(filters ?? {}) } as Record<string, string | number | undefined>)
    return (await httpRequest<PurchaseOrdersResponse>(`${base}${query}`, comoProveedor))!
  },

  async getOrderById(orderId: string): Promise<PurchaseOrder> {
    return (await httpRequest<PurchaseOrder>(`${base}/${orderId}`, comoProveedor))!
  },

  async getStats(orders: PurchaseOrder[]): Promise<PurchaseOrderStats> {
    return this.calculateStats(orders)
  },

  async confirmOrder(orderId: string, comment?: string): Promise<void> {
    await httpRequest(`${base}/${orderId}/confirm`, {
      ...comoProveedor,
      method: 'POST',
      body: JSON.stringify({ comment }),
    })
  },

  async rejectOrder(orderId: string, reason: string): Promise<void> {
    await httpRequest(`${base}/${orderId}/reject`, {
      ...comoProveedor,
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  },

  async addComment(orderId: string, comment: string): Promise<void> {
    await httpRequest(`${base}/${orderId}/comments`, {
      ...comoProveedor,
      method: 'POST',
      body: JSON.stringify({ comment }),
    })
  },

  // El cliente no pone Content-Type cuando el cuerpo es FormData, para que el
  // navegador escriba el suyo con el boundary: sin eso, la subida falla.
  /**
   * `flete` solo aplica a proveedores de tipo Fletes (SP-221): la Carta Porte y
   * el soporte firmado viajan con la factura, para que no exista una factura de
   * Fletes sin sus documentos.
   */
  async adjustInvoice(
    orderId: string,
    xmlFile: File,
    lineIds: string[],
    pdfFile?: File,
    flete?: DocumentosDeFlete,
  ): Promise<unknown> {
    const formData = new FormData()
    formData.append('xml', xmlFile) // XML es obligatorio
    if (pdfFile) formData.append('pdf', pdfFile) // PDF es opcional
    formData.append('line_ids', lineIds.join(','))

    if (flete) {
      // En multipart todo va como texto.
      formData.append('carta_porte_aplica', flete.cartaPorteAplica ? 'true' : 'false')
      if (flete.cartaPorteXml) formData.append('cartaPorteXml', flete.cartaPorteXml)
      if (flete.cartaPortePdf) formData.append('cartaPortePdf', flete.cartaPortePdf)
      if (flete.remisionFirmada) formData.append('remisionFirmada', flete.remisionFirmada)
      if (flete.hojaEntregas) formData.append('hojaEntregas', flete.hojaEntregas)
    }

    return await httpRequest(`${base}/${orderId}/invoices`, {
      ...comoProveedor,
      method: 'POST',
      body: formData,
    })
  },

  async replaceInvoice(orderId: string, invoiceId: string, xmlFile: File, pdfFile?: File): Promise<Partial<PurchaseOrderInvoice>> {
    const formData = new FormData()
    formData.append('xml', xmlFile)
    if (pdfFile) formData.append('pdf', pdfFile)

    return (await httpRequest<Partial<PurchaseOrderInvoice>>(
      `${base}/${orderId}/invoices/${invoiceId}`,
      { ...comoProveedor, method: 'PATCH', body: formData },
    )) ?? {}
  },

  async adjustEvidence(orderId: string, files: File[]): Promise<void> {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))

    await httpRequest(`${base}/${orderId}/evidence`, {
      ...comoProveedor,
      method: 'POST',
      body: formData,
    })
  },

  async uploadAttachment(
    orderId: string,
    file: File,
    type?: string,
    description?: string
  ): Promise<{ message: string; url: string }> {
    const formData = new FormData()
    formData.append('file', file)
    if (type) formData.append('type', type)
    if (description) formData.append('description', description)

    return (await httpRequest<{ message: string; url: string }>(
      `${base}/${orderId}/attachments`,
      { ...comoProveedor, method: 'POST', body: formData },
    ))!
  },

  async getAttachments(orderId: string): Promise<PurchaseOrderAttachment[]> {
    return (await httpRequest<PurchaseOrderAttachment[]>(`${base}/${orderId}/attachments`, comoProveedor)) ?? []
  },

  async getComments(orderId: string): Promise<PurchaseOrderComment[]> {
    return (await httpRequest<PurchaseOrderComment[]>(`${base}/${orderId}/comments`, comoProveedor)) ?? []
  },

  /**
   * Si el proveedor puede facturar ahora mismo, y si no, por que (C-09).
   *
   * El cuerpo del bloqueo es el mismo que el del 422 del upload, asi que se le
   * pasa tal cual a BloqueoFacturacion sin traducir nada.
   *
   * Nunca tumba el flujo: si la consulta falla —sin red, un 500—, se deja
   * pasar y el upload vuelve a revisar. Bloquear al proveedor porque una
   * consulta de cortesia no respondio seria peor que dejarlo intentar.
   */
  async getEstadoDeFacturacion(): Promise<EstadoDeFacturacion> {
    try {
      return (
        (await httpRequest<EstadoDeFacturacion>(
          `${base}/invoicing-status`,
          comoProveedor,
        )) ?? { bloqueado: false }
      )
    } catch {
      return { bloqueado: false }
    }
  },

  async getAvailableLines(orderId: string): Promise<PurchaseOrderLine[]> {
    return (await httpRequest<PurchaseOrderLine[]>(`${base}/${orderId}/available-lines`, comoProveedor)) ?? []
  },

  async getInvoices(orderId: string): Promise<PurchaseOrderInvoice[]> {
    return (await httpRequest<PurchaseOrderInvoice[]>(`${base}/${orderId}/invoices`, comoProveedor)) ?? []
  },

  async getPayments(orderId: string, invoiceId: string): Promise<PurchaseOrderPayment[]> {
    return (await httpRequest<PurchaseOrderPayment[]>(
      `${base}/${orderId}/invoices/${invoiceId}/payments`,
      comoProveedor,
    )) ?? []
  },

  async getAllPayments(orderId: string): Promise<PurchaseOrderPayment[]> {
    const invoices = await this.getInvoices(orderId)
    if (invoices.length === 0) return []

    const paymentsArrays = await Promise.all(
      invoices.map((invoice) => this.getPayments(orderId, invoice.id)),
    )
    return paymentsArrays.flat()
  },

  async uploadComplement(
    orderId: string,
    paymentId: string,
    xmlFile: File,
    pdfFile?: File
  ): Promise<{
    message: string
    xml_url: string
    pdf_url: string | null
    status: string
    validation_notes: string | null
  }> {
    const formData = new FormData()
    formData.append('xml', xmlFile)
    if (pdfFile) {
      formData.append('pdf', pdfFile)
    }

    return (await httpRequest(`${base}/${orderId}/payments/${paymentId}/complement`, {
      ...comoProveedor,
      method: 'POST',
      body: formData,
    }))!
  },

  formatCurrency(amount: string | number, currency: string): string {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount
    const code = (currency || 'MXN').toUpperCase()
    return `${code} $${num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  },

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  },

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      draft: 'Borrador',
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      rejected: 'Rechazada',
      in_progress: 'En Progreso',
      completed: 'Completada',
      cancelled: 'Cancelada'
    }
    return labels[status] || status
  },

  calculateStats(orders: PurchaseOrder[]): PurchaseOrderStats {
    const total = orders.length
    const confirmed = orders.filter(o => o.status === 'confirmed').length
    const rejected = orders.filter(o => o.status === 'rejected').length
    // Added for compatibility with RH view stats
    const pending = orders.filter(o => o.status === 'pending' || o.status === 'draft').length
    const inProgress = orders.filter(o => o.status === 'in_progress').length
    const completed = orders.filter(o => o.status === 'completed').length
    const cancelled = orders.filter(o => o.status === 'cancelled').length

    return {
      total,
      confirmed,
      rejected,
      pending,
      inProgress,
      completed,
      cancelled,
      confirmedPercentage: total > 0 ? (confirmed / total) * 100 : 0,
      rejectedPercentage: total > 0 ? (rejected / total) * 100 : 0
    }
  }
}
